import { useState, useEffect } from "react";
import { arrayMove } from "@dnd-kit/sortable";
import "./FLFIcon.css";
import { NotifyConfirm, Toast } from "../../../../components/notify/Notify";
import { compareTwoArrayDnD } from "../../../../utils/helper";
import useCurrentActionCache from "../../../../hooks/useCurrentActionCache";
import { CACHE_ICON } from "../../../../constants/actionCache";
import {
  getFLFIcon,
  removeIcon,
  sortFLFIcon,
} from "../../../../apis/FLF/icon.api";
import { path } from "../../../../constants/path";
import { useGetPermissionAccessControl } from "../../../../context/PermissionAccessControl";
import { useCallback } from "react";
import ControlPanelSystem from "src/components/controlPanelSystem/ControlPanelSystem";
import useControlPanel from "src/hooks/useControlPanel";
import FLFIconControlPanel from "../../components/ControlPanel/FLFIconControlPanel";
import { useMemo } from "react";
import Pagination from "src/components/Pagination/Pagination";
import { dataStateType } from "src/constants/status";
import IconTable from "../../components/Table/IconTable";
import IconPanelOptional from "../../components/ControlPanel/PanelOptional/IconPanelOptional";

const initIconData = { list: [], total_record: 0 };

const FLFIcon = () => {
  const {
    page,
    searchKey,
    numberPage,
    setNumberPage,
    limit,
    setLimit,
    status,
    setStatus,
    startIndex,
    endIndex,
    setStartDate,
    setEndDate,
    handleInputChange,
    handleChangePage,
  } = useControlPanel();
  const permissionsAccessControl = useGetPermissionAccessControl();
  const { currentActionCache, setCurrentActionCache } = useCurrentActionCache();
  const [iconData, setIconData] = useState(initIconData);
  const [listIconId, setListIconId] = useState([]);
  const [queueSortApi, setQueueSortApi] = useState([]);
  const [isSorted, setIsSorted] = useState(false);
  const [filterIcons, setFilterIcons] = useState([]);
  const [iconType, setIconType] = useState("device");

  const fetchIotIcons = useCallback(() => {
    getFLFIcon(
      {
        page,
        iconType,
      },
      (result) => {
        setIconData({ ...result });
        setFilterIcons([...result.list]);
        setListIconId([...result.list?.map((i) => i.id)]);
      }
    );
  }, [iconType, page]);

  useEffect(() => {
    fetchIotIcons();
  }, [fetchIotIcons]);

  useEffect(() => {
    setNumberPage(page);
  }, [page, setNumberPage]);

  const updateFilteredIcons = useCallback((icons) => {
    setFilterIcons(icons);
    setListIconId(icons.map((icon) => icon.id));
  }, []);

  /* ---- handle search ----- */
  const searchByKeyWord = useCallback(
    (icons, key) => {
      if (status === dataStateType.INACTIVE) {
        return [];
      }
      return icons.filter((icon) =>
        icon.deviceName.toLowerCase().includes(key.toLowerCase().trim())
      );
    },
    [status]
  );

  const handleSearch = useCallback(
    (value) => {
      const filteredIcons = value
        ? searchByKeyWord(iconData.list, value)
        : iconData.list;
      updateFilteredIcons(filteredIcons);
    },
    [iconData.list, searchByKeyWord, updateFilteredIcons]
  );

  useEffect(() => {
    handleSearch(searchKey);
  }, [searchKey, handleSearch]);

  /* ---- handle sort and DnD ----- */
  const handleSort = useCallback(() => {
    sortFLFIcon({ queueSort: queueSortApi }, () => {
      setQueueSortApi([]);
      setIsSorted(false);
      setCurrentActionCache(CACHE_ICON);
      fetchIotIcons();
    });
  }, [fetchIotIcons, queueSortApi, setCurrentActionCache]);

  const handleDragEnd = (event) => {
    const { active, over } = event;

    // kiểm tra nếu không tồn tại over (kéo quá size thì return tránh lỗi)
    if (!over) return;

    if (active.id !== over.id) {
      // lấy vị trí cũ (từ thằng active)
      const oldIndex = filterIcons.findIndex((icon) => icon.id === active.id);

      // lấy vị trí mới (từ thằng order)
      const newIndex = filterIcons.findIndex((icon) => icon.id === over.id);

      const newOrder = arrayMove(filterIcons, oldIndex, newIndex);
      const newOrderIds = newOrder.map((c) => c.id);

      // check xem có thay đổi thì hiện nút xác nhận cập nhật
      const isOrderChanged = !compareTwoArrayDnD(listIconId, newOrderIds);
      setFilterIcons([...newOrder]);
      if (isOrderChanged) {
        setIsSorted(true);
        setQueueSortApi((prev) => [
          ...prev,
          {
            activeIconId: active.id,
            overIconId: over.id,
            iconType,
          },
        ]);
      } else {
        setIsSorted(false);
        setQueueSortApi([]);
      }
    }
  };

  // Action Delete
  const handleDelete = (iconDelete) => {
    if (iconDelete.devices.length) {
      Toast("warning", "Icon đang được sử dụng, không thể xóa");
    } else {
      NotifyConfirm("warning", "Are you delete all ?", (result) => {
        if (result.isConfirmed) {
          removeIcon(iconDelete.id, setCurrentActionCache(CACHE_ICON));
          setIconData((prev) => ({
            ...prev,
            list: prev.list.filter((icon) => icon.id !== iconDelete.id),
            total_record: prev.total_record - 1,
          }));
          updateFilteredIcons((prev) =>
            prev.filter((partner) => partner.id !== iconDelete.id)
          );
        }
      });
    }
  };

  /* ---- handle change icon type ----- */

  const handleChangeIconType = (value) => {
    setIconType(value);
    setNumberPage(1);
  };

  /* ---- result for render ----- */

  const resultFilter = useMemo(
    () => searchByKeyWord(filterIcons, searchKey),
    [filterIcons, searchKey, searchByKeyWord]
  );

  const listItems = useMemo(
    () => resultFilter.slice(startIndex, endIndex),
    [resultFilter, startIndex, endIndex]
  );

  return (
    <div>
      {/** control pan */}
      <FLFIconControlPanel
        isSorted={isSorted}
        handleSort={handleSort}
        currentActionCache={currentActionCache}
        permissionsAccessControl={permissionsAccessControl}
        page={numberPage}
        iconType={iconType}
        limit={limit}
      >
        <ControlPanelSystem
          searchKey={searchKey}
          handleInputChange={(e) => handleInputChange(e, handleSearch)}
          limit={limit}
          setLimit={setLimit}
          setNumberPage={setNumberPage}
          status={status}
          setStatus={setStatus}
          setStartDate={setStartDate}
          setEndDate={setEndDate}
          isDisplayDatePicker={false}
        />

        <IconPanelOptional
          onChange={handleChangeIconType}
          iconType={iconType}
        />
      </FLFIconControlPanel>

      <IconTable
        data={listItems}
        handleDelete={handleDelete}
        status={dataStateType.ACTIVE}
        onDragEnd={handleDragEnd}
      />

      <Pagination
        pathname={path.flfIcon}
        page={numberPage}
        handleChangePage={(page) => handleChangePage(page)}
        limit={limit}
        pageSize={resultFilter.length}
      />
    </div>
  );
};

export default FLFIcon;
